// Dependencies

import styled from 'styled-components';

// Components

import { Typography } from '../../Typography';

import { RedArrow } from '../../../../assets/icons/RedArrow';

import { GreenArrow } from '../../../../assets/icons/GreenArrow';

import { InputRadio } from '../../../inputs/InputRadio';

import { CheckBox } from '../../../inputs/CheckBox';

import InputCalendar from '../../../inputs/Calendar/index';

// Types

import { cellProps, columnNames, trProps } from './types';

import DropDownInput from '../../../inputs/DropDown/DropDownInput';

import SearchableDropdown from '../../../inputs/SearchableDropdown';

import MultiSelectDropdown from '../../../inputs/DropDown/MultiSelectDropdown';

export const Container = styled.div``;

export const FiltersContainer = styled.div`
  background-color: ${({ theme }) =>
    theme.colors.backgroundLila}; /* CAMBIO: Color lila según tema ROIS */

  border-radius: ${({ theme }) => theme.borderRadius.lg};

  padding: ${({ theme }) => theme.spacing.xxl};

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.smallDesktop * theme.percentBaseResponsive}rem) {
    padding: ${({ theme }) => theme.spacing.xl};
  }
`;

export const Title = styled(Typography)`
  font-size: ${({ theme }) => theme.fontSize.L};

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    font-size: ${({ theme }) => theme.fontSize.M};
  }
`;

export const DropDownInputStyled = styled(DropDownInput)`
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.smallDesktop * theme.percentBaseResponsive}rem) {
    max-width: 17rem;
  }

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    max-width: 16rem;
  }
`;

export const SearchableDropdownStyled = styled(SearchableDropdown)`
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.smallDesktop * theme.percentBaseResponsive}rem) {
    max-width: 17rem;
  }

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    max-width: 16rem;
  }
`;

// Mismos breakpoints que DropDownInputStyled / SearchableDropdownStyled —
// sin esto, los MultiSelectDropdown se quedan a su width fijo (27rem para
// inputSize="L") en intermedios donde los otros dropdowns ya han shrinkado.
export const MultiSelectDropdownStyled = styled(MultiSelectDropdown)`
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.smallDesktop * theme.percentBaseResponsive}rem) {
    max-width: 17rem;
  }

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    max-width: 16rem;
  }
`;

export const SubContainerFilters = styled.div`
  display: flex;

  gap: ${({ theme }) => theme.spacing.l};

  flex-wrap: wrap;

  /* Cada filtro mantiene su altura natural cuando uno crece (chips multi-
     select wrap). Sin esto, align-items: stretch (default) hacía que todos
     los inputs de la misma "línea wrap" tomaran la altura del más alto,
     desalineando el grid de filtros visualmente. */
  align-items: flex-start;

  padding-top: ${({ theme }) => theme.spacing.l};
`;

export const ContainerButtons = styled.div`
  display: flex;

  align-items: flex-end;

  /* Alinear el container al bottom de la fila (donde están los inputs de
     los dropdowns), no al top (donde están los labels). El padre
     SubContainerFilters usa align-items: flex-start; sin align-self, los
     iconos quedan flotando arriba, desalineados de los inputs. */
  align-self: flex-end;

  gap: ${({ theme }) => theme.spacing.s};

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    gap: 1rem;
  }
`;

export const TableContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.xl} 0 0;

  overflow-x: auto;
`;

export const Table = styled.table`
  width: 100%;

  text-align: left;
`;

export const TextHeader = styled(Typography)`
  white-space: nowrap;
`;

export const Tbody = styled.tbody``;

export const Th = styled.th`
  border-bottom: 0.1rem solid ${({ theme }) => theme.colors.black};

  padding: 0 ${({ theme }) => theme.spacing.xxs}
    ${({ theme }) => theme.spacing.xxxs} 0;

  p {
    font-size: ${({ theme }) => theme.fontSize.S};
  }

  &:first-child {
    padding-left: 0.8rem;
  }

  &:last-child {
    padding-right: 0.8rem;
  }

  /*
   * Columna de la bandera: el mismo relleno que su celda (abajo), para que el
   * icono de la cabecera y el botón compartan eje. 🔴 'vertical-align: middle':
   * con el 'baseline' del reset, el icono (que no tiene línea base) apoyaba su
   * borde inferior en la línea base del texto y quedaba ~5 px por encima de
   * «Estado», «ROID»…; centrado en la fila cae a la altura de esos textos.
   */
  &[data-export-ignore='true'] {
    padding-left: 0;
    padding-right: ${({ theme }) => theme.spacing.xxs};
    text-align: center;
    vertical-align: middle;
  }

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    p {
      font-size: ${({ theme }) => theme.fontSize.XS};
    }
  }
`;

export const Td = styled.td<cellProps>`
  border-bottom: 0.1rem solid ${({ theme }) => theme.colors.greyLight};

  vertical-align: middle;

  padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.m}
    ${({ theme }) => theme.spacing.xxs} 0;

  &:first-child {
    padding-left: 0.8rem;
  }

  &:last-child {
    padding-right: 0.8rem;
  }

  ${(props) =>
    props.isFigure && {
      paddingLeft: '1.5rem',
    }}

  ${(props) =>
    props.column === columnNames.capitol.id && {
      maxWidth: '12rem',
    }}

  ${(props) =>
    props.column === columnNames.name.id && {
      maxWidth: '14rem',
    }}



  ${(props) =>
    props.column === columnNames.subCapitol.id && {
      maxWidth: '12rem',
    }}



  ${(props) =>
    props.column === columnNames.road.id && {
      maxWidth: '12rem',
    }}

  ${(props) =>
    props.column === columnNames.description.id && {
      maxWidth: '45rem',
    }}

  ${(props) =>
    props.column === columnNames.operators.id && {
      maxWidth: '38rem',
    }}

  /*
   * Columna de la bandera: compacta y con el relleno de su cabecera (mismo
   * eje para el icono de arriba y el botón). Va a la misma distancia de la
   * selección que del texto «Estado»: 0,8 rem de relleno a cada lado del botón
   * (el de la celda de selección, abajo, y el suyo a la derecha) más el margen
   * interior del botón de 44 px, igual en los dos lados.
   */
  &[data-export-ignore='true'] {
    padding-left: 0;
    padding-right: ${({ theme }) => theme.spacing.xxs};
    text-align: center;
  }

  /*
   * La celda de selección deja 2,5 rem a su derecha (el relleno de todas las
   * celdas), pensado para el texto de la columna siguiente. Delante de la
   * bandera eso la separaba el doble de la selección que de «Estado»: solo
   * cuando la sigue la bandera, el mismo 0,8 rem que su cabecera. Las tablas
   * sin bandera (modales, Duplicar) no cambian.
   */
  &:has(+ [data-export-ignore='true']) {
    padding-right: ${({ theme }) => theme.spacing.xxs};
  }

  @media (max-width: ${({ theme }) =>
    theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    ${({ hiddenColumns }) =>
      hiddenColumns > 6 && {
        maxWidth: 'none',
      }}
  }
`;

export const TextCell = styled(Typography)`
  white-space: nowrap;

  overflow: hidden;

  text-overflow: ellipsis;

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    font-size: ${({ theme }) => theme.fontSize.XS};
  }
`;

/**
 * Celda que respeta los saltos de línea del valor.
 *
 * `TextCell` lleva `white-space: nowrap` + elipsis, que es lo que quiere una
 * tabla densa — pero colapsa los `\n`. Hace falta para lo que NO cabe en un
 * solo valor: un recorrido de vigilancia sobre varias carreteras apila una
 * línea por carretera (nombre y sus PK, alineados entre columnas para que la
 * fila se lea en horizontal). Fundirlos en un rango único sería mentir — los PK
 * de una troncal y su ramal no son comparables.
 *
 * Sin saltos de línea se comporta igual que `TextCell`.
 */
/**
 * 🔴 `white-space: pre`, NO `pre-line`.
 *
 * Los dos respetan los saltos de línea que ponemos nosotros, pero `pre-line`
 * además deja que el navegador PARTA la línea donde quiera — y `TextCell` trae
 * `nowrap` precisamente para que no lo haga. La columna de carretera es de
 * cuatro caracteres («Crt.»), así que con `pre-line` un simple «C-25» se rompía
 * por el guion y salía en dos líneas, en TODAS las tablas que comparten esa
 * columna, no solo en las del recorrido.
 *
 * (Y el porqué de este comentario aquí arriba y no dentro: los acentos graves
 * de `pre` cerrarían el template literal de styled-components.)
 */
export const MultiLineCell = styled(TextCell)`
  white-space: pre;
`;

export const FigureRed = styled.figure`
  width: 2.5rem;

  height: 2.5rem;

  border-radius: 100%;

  background-color: ${({ theme }) => theme.colors.clearRed};

  display: inline-flex;

  align-items: center;

  justify-content: center;
`;

export const FigureGreen = styled(FigureRed)`
  background-color: ${({ theme }) => theme.colors.softGreen};
`;

export const TitleInvisible = styled(Typography)`
  opacity: 0;

  position: absolute;
`;

export const GreenArrowStyled = styled(GreenArrow)`
  width: 0.8rem;

  height: 1.1rem;

  color: ${({ theme }) => theme.colors.forestGreen};
`;

export const RedArrowStyled = styled(RedArrow)`
  width: 0.8rem;

  height: 1.1rem;

  color: ${({ theme }) => theme.colors.red};
`;

export const InputRadioContainer = styled.div``;

export const InputRadioStyled = styled(InputRadio)``;

export const InputCheckContainer = styled.div``;

export const CheckBoxStyled = styled(CheckBox)``;

export const Tr = styled.tr<trProps>`
  cursor: pointer;
`;

export const InputCalendarStyled = styled(InputCalendar)``;
